import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import QRCode from 'qrcode';
import { changePasswordSchema, mfaConfirmSchema, type MfaSetupResponse, type SessionInfo } from '@agency/contracts';
import { Badge, Button, EmptyState, Notice, Skeleton, TextField, useToast, scrollRegion } from '@agency/ui';
import { ApiClientError, del, get, post } from '../../lib/api.ts';
import { useAuth } from '../../lib/auth.tsx';
import { describeDevice } from '../../lib/device.ts';
import { fmtDate, relative } from '../../lib/format.ts';
import { QueryError } from '../../lib/query-state.tsx';

const errText = (e: unknown) => (e instanceof ApiClientError ? `${e.message}${e.requestId ? ` (Ref ${e.requestId})` : ''}` : 'Something went wrong. Try again.');

export function AccountPage() {
  const { user } = useAuth();
  return (
    <>
      <div className="pagehead">
        <div>
          <h1>Account security</h1>
          <p>{user?.name} · {user?.email} · {user?.roleName}</p>
        </div>
      </div>
      <div className="stack">
        <PasswordSection />
        <MfaSection />
        <SessionsSection />
      </div>
    </>
  );
}

function Section({ title, lead, children }: { title: string; lead?: string; children: React.ReactNode }) {
  const id = `sec-${title.toLowerCase().replace(/\W+/g, '-')}`;
  return (
    <section className="panel" aria-labelledby={id}>
      <h2 id={id}>{title}</h2>
      {lead ? <p className="ag-muted">{lead}</p> : null}
      {children}
    </section>
  );
}

function PasswordSection() {
  const toast = useToast();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const m = useMutation({
    mutationFn: () => post('/account/password', { currentPassword: current, newPassword: next }),
    onSuccess: () => { setCurrent(''); setNext(''); setErrors({}); toast({ tone: 'success', message: 'Password changed. Your other devices were signed out.' }); },
    onError: (e) => {
      if (e instanceof ApiClientError && e.fieldErrors.length) setErrors(Object.fromEntries(e.fieldErrors.map((f) => [f.field, f.message])));
      else toast({ tone: 'error', message: errText(e) });
    },
  });
  return (
    <Section title="Password" lead="Changing it signs you out everywhere else.">
      <form noValidate className="formgrid" onSubmit={(e) => {
        e.preventDefault();
        const p = changePasswordSchema.safeParse({ currentPassword: current, newPassword: next });
        if (!p.success) { setErrors(Object.fromEntries(p.error.issues.map((i) => [String(i.path[0]), i.path[0] === 'currentPassword' ? 'Enter your current password.' : i.message]))); return; }
        setErrors({});
        m.mutate();
      }}>
        <TextField label="Current password" type="password" autoComplete="current-password" required value={current} onChange={(e) => setCurrent(e.target.value)} error={errors.currentPassword} />
        <TextField label="New password" type="password" autoComplete="new-password" required value={next} onChange={(e) => setNext(e.target.value)} hint="At least 12 characters." error={errors.newPassword} />
        <div><Button type="submit" variant="primary" loading={m.isPending}>Change password</Button></div>
      </form>
    </Section>
  );
}

function MfaSection() {
  const qc = useQueryClient();
  const toast = useToast();
  const status = useQuery<{ enabled: boolean; recoveryCodesRemaining: number }, ApiClientError>({ queryKey: ['mfa'], queryFn: () => get('/account/mfa') });
  const [setup, setSetup] = useState<MfaSetupResponse | null>(null);
  const [qr, setQr] = useState('');
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [disabling, setDisabling] = useState(false);
  const [pw, setPw] = useState('');
  const [dcode, setDcode] = useState('');

  useEffect(() => {
    let live = true;
    if (setup) void QRCode.toDataURL(setup.otpauthUrl, { margin: 1, width: 192 }).then((u) => live && setQr(u));
    return () => { live = false; };
  }, [setup]);

  const refresh = async () => { await Promise.all([qc.invalidateQueries({ queryKey: ['mfa'] }), qc.invalidateQueries({ queryKey: ['me'] })]); };
  const start = useMutation({ mutationFn: () => post<MfaSetupResponse>('/account/mfa/setup'), onSuccess: (r) => { setSetup(r); setError(null); }, onError: (e) => setError(errText(e)) });
  const confirm = useMutation({
    mutationFn: () => post<{ recoveryCodes: string[] }>('/account/mfa/confirm', { code }),
    onSuccess: async (r) => { setCodes(r.recoveryCodes); setSetup(null); setCode(''); setError(null); await refresh(); },
    onError: (e) => setError(errText(e)),
  });
  const disable = useMutation({
    mutationFn: () => post('/account/mfa/disable', { password: pw, code: dcode }),
    onSuccess: async () => { setDisabling(false); setPw(''); setDcode(''); setError(null); toast({ tone: 'success', message: 'Two-step verification is off.' }); await refresh(); },
    onError: (e) => setError(errText(e)),
  });

  if (status.isLoading) return <Section title="Two-step verification"><Skeleton width="100%" height={80} /></Section>;
  if (status.isError) return <Section title="Two-step verification"><QueryError error={status.error} onRetry={() => void status.refetch()} /></Section>;

  if (codes) {
    const text = codes.join('\n');
    return (
      <Section title="Save your recovery codes" lead="Each code signs you in once if you lose your authenticator. They are shown only now.">
        <Notice tone="warning" title="Store these somewhere safe" live={false}>Anyone with a recovery code and your password can sign in. Other devices were signed out when you turned this on.</Notice>
        <pre className="mono-block" aria-label="Recovery codes">{text}</pre>
        <div className="actions__row">
          <Button icon="check" onClick={() => void navigator.clipboard?.writeText(text).then(() => toast({ tone: 'success', message: 'Recovery codes copied.' }))}>Copy codes</Button>
          <a className="ag-btn" style={{ textDecoration: 'none' }} download="recovery-codes.txt" href={`data:text/plain;charset=utf-8,${encodeURIComponent(text + '\n')}`}>Download .txt</a>
          <Button variant="primary" onClick={() => setCodes(null)}>I’ve saved them</Button>
        </div>
      </Section>
    );
  }

  if (status.data!.enabled) {
    return (
      <Section title="Two-step verification" lead="Sign-in asks for a code from your authenticator app after the password.">
        <p><Badge tone="success">On</Badge> <span className="ag-muted">{status.data!.recoveryCodesRemaining} recovery codes left</span></p>
        {error ? <Notice tone="error" title="Could not turn it off">{error}</Notice> : null}
        {!disabling ? <div><Button onClick={() => setDisabling(true)}>Turn off…</Button></div> : (
          <form noValidate className="formgrid" onSubmit={(e) => { e.preventDefault(); disable.mutate(); }}>
            <TextField label="Password" type="password" autoComplete="current-password" required value={pw} onChange={(e) => setPw(e.target.value)} />
            <TextField label="Authenticator or recovery code" autoComplete="one-time-code" required value={dcode} onChange={(e) => setDcode(e.target.value)} />
            <div className="actions__row"><Button type="submit" variant="danger" loading={disable.isPending}>Turn off two-step verification</Button><Button onClick={() => { setDisabling(false); setError(null); }}>Cancel</Button></div>
          </form>
        )}
      </Section>
    );
  }

  return (
    <Section title="Two-step verification" lead="Protect your account with a code from an authenticator app (1Password, Authy, Google Authenticator and others).">
      <p><Badge>Off</Badge></p>
      {error ? <Notice tone="error" title="Could not continue">{error}</Notice> : null}
      {!setup ? <div><Button variant="primary" loading={start.isPending} onClick={() => start.mutate()}>Set up</Button></div> : (
        <div className="mfa-setup">
          <div>
            {qr ? <img src={qr} width={192} height={192} alt="QR code for your authenticator app" /> : <Skeleton width={192} height={192} />}
          </div>
          <form noValidate className="formgrid" onSubmit={(e) => {
            e.preventDefault();
            const p = mfaConfirmSchema.safeParse({ code });
            if (!p.success) { setError(p.error.issues[0]?.message ?? 'Enter the 6-digit code.'); return; }
            confirm.mutate();
          }}>
            <p>Scan the QR code, or enter this key manually:</p>
            <code className="mono-inline" aria-label="Manual entry key">{setup.secret.match(/.{1,4}/g)?.join(' ')}</code>
            <TextField label="6-digit code from the app" inputMode="numeric" autoComplete="one-time-code" required value={code} onChange={(e) => setCode(e.target.value)} />
            <div className="actions__row"><Button type="submit" variant="primary" loading={confirm.isPending}>Turn on</Button><Button onClick={() => { setSetup(null); setCode(''); setError(null); }}>Cancel</Button></div>
          </form>
        </div>
      )}
    </Section>
  );
}

function SessionsSection() {
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery<SessionInfo[], ApiClientError>({ queryKey: ['sessions'], queryFn: () => get('/account/sessions') });
  const refresh = () => qc.invalidateQueries({ queryKey: ['sessions'] });
  const revoke = useMutation({ mutationFn: (id: string) => del(`/account/sessions/${id}`), onSuccess: async () => { toast({ tone: 'success', message: 'Device signed out.' }); await refresh(); }, onError: (e) => toast({ tone: 'error', message: errText(e) }) });
  const others = useMutation({ mutationFn: () => post<{ revoked: number }>('/account/sessions/revoke-others'), onSuccess: async (r) => { toast({ tone: 'success', message: r.revoked ? `Signed out ${r.revoked} other device${r.revoked === 1 ? '' : 's'}.` : 'No other devices were signed in.' }); await refresh(); }, onError: (e) => toast({ tone: 'error', message: errText(e) }) });
  return (
    <Section title="Where you’re signed in" lead="Sessions end automatically after a period of inactivity or when they reach their maximum age.">
      {q.isLoading ? <Skeleton width="100%" height={100} /> : q.isError ? <QueryError error={q.error} onRetry={() => void q.refetch()} /> : q.data!.length === 0 ? <EmptyState title="No active sessions" icon="lock" /> : (
        <>
          <div className="ag-table-wrap" {...scrollRegion('Active sessions')}>
            <table className="ag-table">
              <caption className="ag-visually-hidden">Active sessions</caption>
              <thead><tr><th scope="col">Device</th><th scope="col">Network address</th><th scope="col">Signed in</th><th scope="col">Last active</th><th scope="col"><span className="ag-visually-hidden">Actions</span></th></tr></thead>
              <tbody>
                {q.data!.map((s) => (
                  <tr key={s.id}>
                    <td><span className="ag-cell-2"><span>{describeDevice(s.userAgent)}</span>{s.current ? <span><Badge tone="accent">This device</Badge></span> : <span>&nbsp;</span>}</span></td>
                    <td className="ag-mono">{s.ip ?? '—'}</td>
                    <td>{fmtDate(s.createdAt)}</td>
                    <td>{s.lastSeenAt ? relative(s.lastSeenAt) : '—'}</td>
                    <td style={{ textAlign: 'right' }}>{s.current ? null : <Button size="sm" loading={revoke.isPending && revoke.variables === s.id} onClick={() => revoke.mutate(s.id)} aria-label={`Sign out ${describeDevice(s.userAgent)} from ${s.ip ?? 'unknown address'}`}>Sign out</Button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {q.data!.length > 1 ? <div><Button loading={others.isPending} onClick={() => others.mutate()}>Sign out all other devices</Button></div> : null}
        </>
      )}
    </Section>
  );
}
